import { TRPCClientError } from "@trpc/client";
import {
    errorClassName,
    FAILED_FETCH_MESSAGES,
    ReportErrorOptions,
    shouldSkipReporting,
} from "util/error-tracking";
import { ClientHttpError } from "util/http";

export type TrpcErrorReport = (error: Error, options: ReportErrorOptions) => void;

const isNetworkFailure = (error: unknown): boolean =>
    error instanceof TRPCClientError &&
    !error.data &&
    error.cause instanceof TypeError &&
    FAILED_FETCH_MESSAGES.includes(error.cause.message);

export const isTrpcErrorReportable = (error: unknown): boolean =>
    !shouldSkipReporting(error) && !isNetworkFailure(error);

const recordOf = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const trpcFailureKind = (error: unknown): string => {
    const { data, cause } = recordOf(error);
    const { code } = recordOf(data);
    if (typeof code === "string") {
        return code;
    }
    if (cause instanceof ClientHttpError) {
        return `HTTP ${cause.statusCode}`;
    }
    return errorClassName(cause ?? error);
};

export const trpcPathFromKey = (key: unknown): string => {
    const segments = Array.isArray(key) ? key[0] : undefined;
    return Array.isArray(segments) ? segments.join(".") : "unknown";
};

export const makeTrpcErrorReporter =
    (report: TrpcErrorReport) =>
    (error: unknown, trpcPath: string): void => {
        if (!isTrpcErrorReportable(error)) {
            return;
        }
        const reportable = error as Error;
        report(reportable, {
            fingerprint: `trpc:${trpcPath}:${trpcFailureKind(error)}`,
            params: { message: reportable?.message },
            context: { trpcPath },
        });
    };
